/**
 * Durable session persistence.
 *
 * Root causes this guards against:
 *
 * 1. supabase-js deletes the stored session from storage whenever a token
 *    refresh comes back with a non-retryable auth error. When a phone wakes up
 *    or the app is re-opened, several refreshes can fire at once (library
 *    recover-on-visibility, our own revalidation, a second tab) and the loser of
 *    that race gets a 400 "Invalid Refresh Token: Already Used". The library
 *    then wipes the session and emits SIGNED_OUT — the classic "logged out every
 *    time I close the app" symptom.
 *
 * 2. A recovery attempt made while the device has no connection yet (the very
 *    first moment after reopening the app) used to fail once and give up, which
 *    dropped the user on the login screen even though the session was fine.
 *
 * 3. localStorage alone is fragile on mobile browsers (storage pressure,
 *    privacy eviction). We therefore mirror the refresh token into IndexedDB as
 *    well and re-seed localStorage from it on start-up.
 *
 * The copy is removed only when the user taps sign out, or when the server tells
 * us the refresh token is genuinely dead.
 */

const BACKUP_KEY = "coachai.auth.session.backup.v1";
const MANUAL_SIGNOUT_KEY = "coachai.auth.manual-signout.v1";

const IDB_NAME = "coachai-auth";
const IDB_STORE = "session";
const IDB_KEY = "backup";

type Backup = {
  refresh_token: string;
  access_token: string;
  user_id: string | null;
  at: number;
};

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * IndexedDB mirror (best effort — every call resolves, never throws) *
 * ------------------------------------------------------------------ */

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbWrite(value: Backup | null): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      const store = tx.objectStore(IDB_STORE);
      if (value) store.put(value, IDB_KEY);
      else store.delete(IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
  db.close();
}

async function idbRead(): Promise<Backup | null> {
  const db = await openDb();
  if (!db) return null;
  const value = await new Promise<Backup | null>((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = () => resolve((req.result as Backup) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  db.close();
  return value;
}

/* ---------------------------- public API ---------------------------- */

export function backupSession(session: {
  access_token?: string | null;
  refresh_token?: string | null;
  user?: { id?: string } | null;
} | null): void {
  if (!session?.refresh_token || !session.access_token) return;
  const payload: Backup = {
    refresh_token: session.refresh_token,
    access_token: session.access_token,
    user_id: session.user?.id ?? null,
    at: Date.now(),
  };
  try {
    window.localStorage.setItem(BACKUP_KEY, JSON.stringify(payload));
    window.localStorage.removeItem(MANUAL_SIGNOUT_KEY);
  } catch {
    /* storage unavailable — the IndexedDB mirror below still applies */
  }
  void idbWrite(payload);
}

export function markManualSignOut(): void {
  try {
    window.localStorage.setItem(MANUAL_SIGNOUT_KEY, String(Date.now()));
    window.localStorage.removeItem(BACKUP_KEY);
  } catch {
    /* ignore */
  }
  void idbWrite(null);
}

export function wasManualSignOut(): boolean {
  try {
    return window.localStorage.getItem(MANUAL_SIGNOUT_KEY) !== null;
  } catch {
    return false;
  }
}

export function getSessionBackup(): Backup | null {
  const backup = read<Backup>(BACKUP_KEY);
  if (!backup?.refresh_token || !backup.access_token) return null;
  return backup;
}

function clearBackup(): void {
  try {
    window.localStorage.removeItem(BACKUP_KEY);
  } catch {
    /* ignore */
  }
  void idbWrite(null);
}

/**
 * Copy the IndexedDB mirror back into localStorage when localStorage lost it
 * (evicted, cleared, or written by a build that never had it). Runs once on
 * start-up, before we decide whether anybody is signed in.
 */
export async function primeSessionBackup(): Promise<boolean> {
  if (wasManualSignOut()) return false;
  if (getSessionBackup()) return true;
  const mirrored = await idbRead();
  if (!mirrored?.refresh_token || !mirrored.access_token) return false;
  try {
    window.localStorage.setItem(BACKUP_KEY, JSON.stringify(mirrored));
  } catch {
    /* ignore */
  }
  return true;
}

type MinimalClient = {
  auth: {
    setSession: (args: { access_token: string; refresh_token: string }) => Promise<{
      data: { session: unknown | null };
      error: { message: string } | null;
    }>;
  };
};

let inFlight: Promise<boolean> | null = null;

const DEAD_TOKEN_HINTS = [
  "invalid refresh token",
  "refresh token not found",
  "user not found",
  "user is banned",
];

/**
 * Try to restore a session that supabase-js dropped for a reason other than an
 * explicit sign-out. Retries a few times with backoff so a momentary lack of
 * connection (exactly what happens when a phone reopens the app) can never sign
 * the user out. Returns true when a session is back.
 */
export async function recoverSession(client: MinimalClient): Promise<boolean> {
  if (wasManualSignOut()) return false;
  if (inFlight) return inFlight;
  inFlight = attemptRecovery(client).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

const DELAYS_MS = [0, 800, 2_000, 5_000];

async function attemptRecovery(client: MinimalClient): Promise<boolean> {
  for (const delay of DELAYS_MS) {
    if (delay) await new Promise((r) => setTimeout(r, delay));
    if (wasManualSignOut()) return false;
    // Always re-read: an earlier attempt (or another tab) may have rotated it.
    const backup = getSessionBackup();
    if (!backup) return false;
    try {
      const { data, error } = await client.auth.setSession({
        access_token: backup.access_token,
        refresh_token: backup.refresh_token,
      });
      if (data?.session) return true;
      const message = error?.message?.toLowerCase() ?? "";
      // "Already used" means another refresh won the race — the session is
      // alive, we just hold a stale copy. Retrying picks up the fresh token.
      if (DEAD_TOKEN_HINTS.some((hint) => message.includes(hint))) {
        clearBackup();
        return false;
      }
      // Anything else (network error, 5xx, rate limit) is retryable.
    } catch {
      /* offline — retry */
    }
  }
  // Out of attempts but the token may still be good: keep the backup so the
  // next app open / reconnect can try again.
  return false;
}
